import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useBlocker } from '@tanstack/react-router'
import {
  DndContext,
  DragOverlay,
  pointerWithin,
} from '@dnd-kit/core'
import { clsx } from 'clsx'
import { CalendarX2, Copy, Flag, Lock } from 'lucide-react'
import { useMySquad }               from '../queries'
import { useLeagueDetail, useLeagueGameweekStatus } from '@/features/league/queries'
import { useUserLeagues }            from '@/features/league/hooks/useUserLeagues'
import { useSquadPicker } from '../hooks/useSquadPicker'
import { useSquadDnd }    from '../hooks/useSquadDnd'
import { usePlayerStatsSheet } from '../hooks/usePlayerStatsSheet'
import { usePlayerSearch } from '../hooks/usePlayerSearch'
import { useMediaQuery }  from '@/shared/hooks/useMediaQuery'
import { SquadPickerHeader }  from './SquadPickerHeader'
import { TacticalPitch }      from './TacticalPitch'
import { BenchSection }       from './BenchSection'
import { PlayerListPanel }    from './PlayerListPanel'
import { PlayerStatsSheet }   from './PlayerStatsSheet'
import { ConfirmPenaltyModal } from './ConfirmPenaltyModal'
import { ConfirmDiscardModal } from './ConfirmDiscardModal'
import { CopySquadModal }      from './CopySquadModal'
import type { CopyPayload, PlayerFilter } from '../types'
import type { Position }       from '@/types/domain'
import styles from './SquadPicker.module.css'

// ── Ghost card rendered in DragOverlay ────────────────────────────────────────

function DragGhostCard({ name }: { name: string }) {
  return (
    <div className={styles.dragGhost}>
      <div className={styles.dragGhostCircle} />
      <span className={styles.dragGhostName}>{name}</span>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

type SquadPickerProps = {
  leagueId:             string
  selectedGwId?:        string | undefined   // when set and GW is finalized, shows historical snapshot (read-only)
  standingGwPoints?:    number | undefined    // last finalized GW points from league standings
  standingTotalPoints?: number | undefined    // cumulative total from league standings
}

type MobileTab = 'escalacao' | 'lista'

// Minimal shape of the from/to locations passed to the router blocker — enough
// to tell a pure tab switch (same pathname + gameweek) from a real leave.
type BlockerLocation = { pathname: string; search: Record<string, unknown> }

export function SquadPicker({ leagueId, selectedGwId, standingGwPoints, standingTotalPoints }: SquadPickerProps) {
  const [mobileTab, setMobileTab] = useState<MobileTab>('escalacao')
  const [isPenaltyModalOpen, setIsPenaltyModalOpen] = useState(false)
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false)
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false)
  const isSplitLayout = useMediaQuery('(min-width: 768px)')

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: savedSquad, isLoading: isLoadingSquad } = useMySquad(leagueId, selectedGwId)
  const { data: leagueDetail }                          = useLeagueDetail(leagueId)
  const { data: userLeagues }                           = useUserLeagues()
  const { data: gwStatus }                              = useLeagueGameweekStatus(leagueId)
  const competitionId = leagueDetail?.competitionId ?? ''

  // Default the player list to total-points order, but fall back to price until a gameweek
  // has actually been scored — before that everyone is on 0 points and points order is noise.
  // (gwStatus is prefetched by the league route loader, so it's cached when the picker mounts.)
  const hasScoredGameweek = gwStatus?.gameweeks.some(g => g.scoresFinalized) ?? false
  const defaultSortBy: PlayerFilter['sortBy'] = hasScoredGameweek ? 'points' : 'price'

  // Copy is offered when the user has at least one peer league in the same competition.
  const peerLeagueCount = useMemo(() => {
    if (!userLeagues || !competitionId) return 0
    return userLeagues.filter(l => l.competitionId === competitionId && l.id !== leagueId).length
  }, [userLeagues, competitionId, leagueId])

  // A peer league with a built squad (non-empty squadPreview) is a real copy source.
  // Drives the empty-state banner — generic, so we don't need to single out a league.
  const hasCopyableSource = useMemo(() => {
    if (!userLeagues || !competitionId) return false
    return userLeagues.some(
      l => l.competitionId === competitionId && l.id !== leagueId && l.squadPreview.length > 0,
    )
  }, [userLeagues, competitionId, leagueId])

  const isHistorical = savedSquad?.isHistoricalSnapshot ?? false
  // Finalized GW the user did not field a team for — show a dedicated empty state,
  // never the ghost pitch (which reads as an editable, half-built squad).
  const isEmptyHistorical = isHistorical && (savedSquad?.players.length ?? 0) === 0

  // Transfer window is open when squad is not locked AND we're not viewing a historical snapshot
  // Squad missing (undefined) → treat as window open until we know better
  const isWindowOpen = !isHistorical && (savedSquad !== undefined ? !savedSquad.isLocked : true)

  const subbedOutIds = useMemo(
    () => new Set(savedSquad?.substitutions?.map(s => s.playerOutId) ?? []),
    [savedSquad?.substitutions],
  )
  const subbedInIds = useMemo(
    () => new Set(savedSquad?.substitutions?.map(s => s.playerInId) ?? []),
    [savedSquad?.substitutions],
  )

  // ── Business logic ────────────────────────────────────────────────────────
  const {
    draft,
    formation,
    budgetRemaining,
    playerCount,
    draftPlayerIds,
    accounting,
    canSave,
    isSaving,
    addPlayer,
    removePlayer,
    reorderBench,
    swapStarterWithBench,
    swapStarters,
    swapGks,
    promoteBench,
    demoteStarter,
    loadFromCopyPayload,
    resetDraft,
    clearDraft,
    save,
  } = useSquadPicker({ leagueId, savedSquad, isWindowOpen, selectedGwId })

  // ── Pitch player click → stats sheet ─────────────────────────────────────
  const {
    selectPlayer:   handlePitchClickPlayer,
    clearSelection: clearPitchSelection,
    playerForSheet: pitchPlayerForSheet,
  } = usePlayerStatsSheet(draft)

  // ── Pitch ghost "+" → filter the player list by that band's position ─────────
  // Lifted player-search hook lets a click on an empty pitch slot drive the list
  // filter. On mobile (pitch and list are separate tabs) also switch to the list.
  const playerSearch = usePlayerSearch(budgetRemaining, defaultSortBy)
  const handleFilterPosition = useCallback((position: Position) => {
    playerSearch.setPosition(position)
    if (!isSplitLayout) setMobileTab('lista')
  }, [playerSearch, isSplitLayout])

  // ── Save gate ─────────────────────────────────────────────────────────────
  // If the pending diff would trigger a penalty, open the confirm modal first.
  // Otherwise, save directly.
  const handleSaveClick = useCallback(() => {
    if (accounting.penaltyPoints < 0) {
      setIsPenaltyModalOpen(true)
    } else {
      save()
    }
  }, [accounting.penaltyPoints, save])

  // ── Unsaved-changes guard ───────────────────────────────────────────────────
  // Block leaving the squad picker (sidebar, browser back/refresh) while the
  // editable draft has unsaved edits — the resolver drives a confirm modal so
  // the user can leave (proceed) or stay (reset).
  const isDirtyAndOpen = useCallback(
    () => draft.isDirty && isWindowOpen,
    [draft.isDirty, isWindowOpen],
  )
  // Allow pure in-page tab switches (same pathname + same gameweek, only the
  // `tab` search param changed) so the user can peek at Partidas/Tabela without
  // discarding the draft. Real leaves and gameweek changes still warn.
  const shouldBlockLeave = useCallback(
    ({ current, next }: { current: BlockerLocation; next: BlockerLocation }) => {
      if (!isDirtyAndOpen()) return false
      const sameLeagueTabSwitch =
        current.pathname === next.pathname && current.search.gw === next.search.gw
      return !sameLeagueTabSwitch
    },
    [isDirtyAndOpen],
  )
  const leaveBlocker = useBlocker({
    shouldBlockFn:      shouldBlockLeave,
    enableBeforeUnload: isDirtyAndOpen,
    withResolver:       true,
  })

  // ── Copy modal ────────────────────────────────────────────────────────────
  // Confirm-before-discard (Fantyx-styled modal) if the draft has unsaved edits.
  const handleCopyClick = useCallback(() => {
    if (draft.isDirty) {
      setIsDiscardModalOpen(true)
      return
    }
    setIsCopyModalOpen(true)
  }, [draft.isDirty])

  const handleDiscardConfirm = useCallback(() => {
    setIsDiscardModalOpen(false)
    setIsCopyModalOpen(true)
  }, [])

  const handleCopyConfirm = useCallback((payload: CopyPayload) => {
    loadFromCopyPayload(payload)
    setIsCopyModalOpen(false)
  }, [loadFromCopyPayload])

  // Close the penalty modal once the save actually completes successfully
  // (isSaving settles back to false and the draft is no longer dirty).
  // On error: draft stays dirty → modal stays open → user can retry or cancel.
  useEffect(() => {
    if (isPenaltyModalOpen && !isSaving && !draft.isDirty) {
      setIsPenaltyModalOpen(false)
    }
  }, [isPenaltyModalOpen, isSaving, draft.isDirty])

  // ── DnD ───────────────────────────────────────────────────────────────────
  const { sensors, activeId, activeDragPlayer, handleDragStart, handleDragOver, handleDragEnd, handleDragCancel } =
    useSquadDnd({ starters: draft.starters, bench: draft.bench, benchGk: draft.benchGk, reorderBench, swapStarterWithBench, swapStarters, swapGks, promoteBench, demoteStarter })

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoadingSquad) {
    return (
      <div className={styles.loading}>
        <div className={styles.skeleton} />
        <div className={styles.skeleton} style={{ width: '70%' }} />
        <div className={styles.skeleton} style={{ width: '50%' }} />
      </div>
    )
  }

  // Empty-state nudge: blank squad here + the user already built one elsewhere in
  // this competition. Disappears the moment they add a player or save. No dismiss
  // needed — it only ever shows on a still-empty squad.
  const showCopyBanner = isWindowOpen && playerCount === 0 && hasCopyableSource

  // ── Pitch panel (shared between desktop left & mobile "escalacao" tab) ────
  const pitchPanel = (
    <div className={styles.pitchPanel}>
      {/* Copy-from-another-league nudge — only on a blank squad */}
      {showCopyBanner && (
        <div className={styles.copyBanner} role="status">
          <Copy size={14} strokeWidth={2.5} className={styles.copyBannerIcon} />
          <span className={styles.copyBannerText}>
            Você já tem uma escalação em outra liga desta competição. Copiar?
          </span>
          <button type="button" className={styles.copyBannerBtn} onClick={handleCopyClick}>
            Copiar
          </button>
        </div>
      )}

      {/* Finalized GW (read-only history) → yellow "Rodada finalizada".
          Empty-history rounds carry their own state, so skip the banner there. */}
      {isHistorical && !isEmptyHistorical && (
        <div className={clsx(styles.lockBanner, styles.lockBannerFinalized)} role="status">
          <Flag size={13} strokeWidth={2.5} />
          Rodada finalizada.
        </div>
      )}

      {/* Current GW locked — window closed but not yet finalized → red. */}
      {!isWindowOpen && !isHistorical && (
        <div className={styles.lockBanner} role="status">
          <Lock size={13} strokeWidth={2.5} />
          Janela de transferências fechada — escalação bloqueada.
        </div>
      )}

      {isEmptyHistorical ? (
        <div className={styles.noSquadState} role="status">
          <CalendarX2 size={30} strokeWidth={1.5} className={styles.noSquadIcon} />
          <p className={styles.noSquadTitle}>Você não escalou um time nesta rodada.</p>
          <p className={styles.noSquadBody}>
            Nenhuma pontuação foi registrada para esta rodada.
          </p>
        </div>
      ) : (
        <>
          <TacticalPitch
            starters={draft.starters}
            isWindowOpen={isWindowOpen}
            subbedOutIds={subbedOutIds}
            onRemovePlayer={removePlayer}
            onClickPlayer={handlePitchClickPlayer}
            onFilterPosition={handleFilterPosition}
          />

          <BenchSection
            benchGk={draft.benchGk}
            bench={draft.bench}
            isWindowOpen={isWindowOpen}
            subbedInIds={subbedInIds}
            onRemove={removePlayer}
            onClickPlayer={handlePitchClickPlayer}
          />

          {/* Substitution log — only shown for finalized GWs with auto-subs */}
          {isHistorical && (savedSquad?.substitutions?.length ?? 0) > 0 && (
            <div className={styles.subLog} role="log" aria-label="Substituições automáticas">
              <div className={styles.subLogTitle}>Substituições automáticas</div>
              {savedSquad!.substitutions!.map((s, i) => (
                <div key={i} className={styles.subLogRow}>
                  <span className={styles.subOut}>↓ {s.playerOutDisplayName}</span>
                  <span className={styles.subArrow}>→</span>
                  <span className={styles.subIn}>↑ {s.playerInDisplayName}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )

  return (
    <div className={styles.root}>
      {/* ── Header ────────────────────────────────────────────────────── */}
      <SquadPickerHeader
        formation={formation}
        playerCount={playerCount}
        budgetRemaining={budgetRemaining}
        totalFantasyPoints={standingTotalPoints ?? savedSquad?.totalFantasyPoints ?? 0}
        gwPoints={(() => {
          if (!savedSquad) return standingGwPoints ?? 0
          if (savedSquad.isHistoricalSnapshot) return savedSquad.gameweekNetPoints ?? 0
          if (isWindowOpen) return standingGwPoints ?? 0
          return savedSquad.players
            .filter(p => p.isStarter)
            .reduce((sum, p) => sum + (p.gwPoints ?? 0), 0)
        })()}
        isWindowOpen={isWindowOpen}
        canSave={canSave}
        isSaving={isSaving}
        isDirty={draft.isDirty}
        accounting={accounting}
        canCopy={isWindowOpen && peerLeagueCount > 0}
        onCopy={handleCopyClick}
        onSave={handleSaveClick}
        onReset={resetDraft}
        onClear={clearDraft}
      />

      {/* ── Mobile tab bar ─────────────────────────────────────────────── */}
      <div className={styles.mobileTabs} role="tablist" aria-label="Seções">
        <button
          role="tab"
          aria-selected={mobileTab === 'escalacao'}
          className={clsx(styles.mobileTab, mobileTab === 'escalacao' && styles.mobileTabActive)}
          onClick={() => setMobileTab('escalacao')}
        >
          Escalação
        </button>
        <button
          role="tab"
          aria-selected={mobileTab === 'lista'}
          className={clsx(styles.mobileTab, mobileTab === 'lista' && styles.mobileTabActive)}
          onClick={() => setMobileTab('lista')}
        >
          Lista de Jogadores
        </button>
      </div>

      {/* ── Content area ───────────────────────────────────────────────── */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* Conditional render — pitchPanel must only exist in the DOM once.
            Rendering it in both layouts simultaneously creates duplicate dnd IDs,
            causing the DragOverlay ghost to snap to (0,0) and drops to fail. */}
        {isSplitLayout ? (
          <div className={styles.splitLayout}>
            <div className={styles.splitLeft}>
              {pitchPanel}
            </div>
            <div className={styles.splitRight}>
              <PlayerListPanel
                draft={draft}
                draftIds={draftPlayerIds}
                isWindowOpen={isWindowOpen}
                competitionId={competitionId}
                budgetRemaining={budgetRemaining}
                search={playerSearch}
                onAdd={addPlayer}
                onRemove={removePlayer}
              />
            </div>
          </div>
        ) : (
          <div className={styles.mobileLayout}>
            {mobileTab === 'escalacao' ? (
              pitchPanel
            ) : (
              <PlayerListPanel
                draft={draft}
                draftIds={draftPlayerIds}
                isWindowOpen={isWindowOpen}
                competitionId={competitionId}
                budgetRemaining={budgetRemaining}
                search={playerSearch}
                onAdd={addPlayer}
                onRemove={removePlayer}
              />
            )}
          </div>
        )}

        {/* DragOverlay — ghost while any squad player is being dragged. Drag ids are stable
            per player ("player-{id}"), so the default drop animation snaps the ghost to the
            dragged player's real new slot after any reorder. */}
        <DragOverlay>
          {activeDragPlayer && (
            <DragGhostCard name={activeDragPlayer.displayName} />
          )}
        </DragOverlay>
      </DndContext>

      {/* Stats sheet — shown immediately on tap using draft data; gwHistory hydrates in background */}
      {pitchPlayerForSheet && (
        <PlayerStatsSheet
          player={pitchPlayerForSheet}
          isInSquad={true}
          isWindowOpen={isWindowOpen}
          onAdd={() => { /* already in squad */ }}
          onRemove={(playerId) => { removePlayer(playerId); clearPitchSelection() }}
          onClose={clearPitchSelection}
        />
      )}

      {/* Penalty confirmation — only when Save would trigger a paid-transfer penalty */}
      {isPenaltyModalOpen && (
        <ConfirmPenaltyModal
          accounting={accounting}
          isSaving={isSaving}
          onConfirm={save}
          onCancel={() => setIsPenaltyModalOpen(false)}
        />
      )}

      {/* Discard-unsaved-changes confirmation before opening the copy flow */}
      {isDiscardModalOpen && (
        <ConfirmDiscardModal
          onConfirm={handleDiscardConfirm}
          onCancel={() => setIsDiscardModalOpen(false)}
        />
      )}

      {/* Unsaved-changes guard — warns before leaving the squad picker with edits.
          Portaled to <body> so it stays visible even when the picker itself is
          hidden (display:none) on another league tab. */}
      {leaveBlocker.status === 'blocked' && createPortal(
        <ConfirmDiscardModal
          title="Sair sem salvar?"
          message="Você tem alterações não salvas na sua escalação. Se sair agora, elas serão perdidas."
          confirmLabel="Sair sem salvar"
          onConfirm={leaveBlocker.proceed}
          onCancel={leaveBlocker.reset}
        />,
        document.body,
      )}

      {/* Copy from another league — staged into the local draft (§13.10) */}
      {isCopyModalOpen && savedSquad && (
        <CopySquadModal
          targetSquadId={savedSquad.squadId}
          currentLeagueId={leagueId}
          currentCompetitionId={competitionId}
          onConfirm={handleCopyConfirm}
          onClose={() => setIsCopyModalOpen(false)}
        />
      )}
    </div>
  )
}
